import { LoggingFacade } from '@vwdfive/myvw-logging';

import { DevicePlatform } from '../../enums';
import { TimeoutError } from '../../errors/TimeoutError';
import { VcfUpdateCapabilityError } from '../../errors/VcfUpdateCapabilityError';

import { evaluate } from './CapabilityUpdateStatusEvaluator';
import { poll } from './PollingWrapper';
import { IVcfCapability, IVcfUpdateStatusResponse, VcfCapabilityClient } from './VcfCapabilityClient';

export class VcfCapabilityService {
    constructor(private readonly vcfCapabilityClient: VcfCapabilityClient, private readonly loggingFacade: LoggingFacade) {
    }

    public async getUserVehicleCapabilities(vin: string, devicePlatform: DevicePlatform): Promise<IVcfCapability[]> {
        const isWCar = devicePlatform === DevicePlatform.WCAR;
        const vcfResponse = await this.vcfCapabilityClient.getUserVehicleCapabilities(vin, isWCar);
        return vcfResponse.data ?? [];
    }

    public isCapabilityEnabled(id: string, capabilities: IVcfCapability[]): boolean {
        return capabilities?.some(capability => capability.id === id && capability.status.length === 0);
    }

    public isAtLeastOneCapabilityEnabled(ids: string[], capabilities: IVcfCapability[]): boolean {
        return capabilities?.some(capability => (ids.includes(capability.id)
            && capability.status.length === 0));
    }

    public isCapabilityDisabledByUser(id: string, capabilities: IVcfCapability[]): boolean {
        const foundCapability = capabilities?.find(capability => capability.id === id);
        if (!foundCapability) {
            return false;
        }
        const allowedStatusCodes = [1004, 1005, 1014, 3005];
        const statusCodes = foundCapability.status;
        if (statusCodes.filter(code => !allowedStatusCodes.includes(code)).length !== 0) {
            return false;
        }
        return (statusCodes.includes(1004) || statusCodes.includes(1005));
    }

    public async updateUserVehicleCapability(vin: string, devicePlatform: DevicePlatform, capabilityId: string, active: boolean): Promise<IVcfCapability[]> {
        const isWCar = devicePlatform === DevicePlatform.WCAR;
        const requestIdResponse = await this.vcfCapabilityClient.setUserVehicleCapabilityEnabled(vin, isWCar, capabilityId, active);
        if (!(requestIdResponse?.data?.requestID)) {
            this.loggingFacade.error('Could not get requestId to update user vehicle capability');
            throw new VcfUpdateCapabilityError();
        }

        const updateStatus = await poll<IVcfUpdateStatusResponse | undefined>(
            () => this.vcfCapabilityClient.getCapabilityUpdateStatus(vin, isWCar, requestIdResponse.data!.requestID),
            evaluate, 1000, 60000);
        this.checkUpdateStatus(updateStatus);

        const capabilities = await this.vcfCapabilityClient.getUserVehicleCapabilities(vin, isWCar);
        if (!(capabilities.data && capabilities.data.length > 0)) {
            this.loggingFacade.error('Could not reload user vehicle capabilities after update');
            throw new VcfUpdateCapabilityError();
        }

        return capabilities.data
    }

    private checkUpdateStatus(updateStatus: IVcfUpdateStatusResponse | undefined) {
        if (!(updateStatus?.data) || updateStatus.data.status === 'pending') {
            this.loggingFacade.error('Could not update user vehicle capability (timeout)');
            throw new TimeoutError();
        }
        if (updateStatus.data.status === 'fail') {
            this.loggingFacade.error('Could not update user vehicle capability (status: fail)');
            throw new VcfUpdateCapabilityError();
        }
    }
}